import React from "react";

/**
 * Cinematic rocket-launch intro — faithful port of the approved
 * Hostinger version (.rocket-stage, .rocket-craft, etc.). Plays once
 * per session via sessionStorage; users with reduced motion get a
 * 600ms fade in/out.
 *
 * Total duration: ~3.6s (rocket flies 2.6s + 0.4s pad + ~600ms text +
 * fade). Skip button removes it instantly.
 */
const SKIP_KEY = "2h_rocket_seen_v1";

export default function RocketIntro({ onDone }) {
  const [show, setShow] = React.useState(() => {
    try { return !sessionStorage.getItem(SKIP_KEY); } catch (_) { return true; }
  });
  const [gone, setGone] = React.useState(false);
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Auto-finish after the launch animation completes
  React.useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => finish(), reduce ? 700 : 3600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Lock scroll while the intro is on screen
  React.useEffect(() => {
    if (!show || gone) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [show, gone]);

  function finish() {
    setGone(true);
    try { sessionStorage.setItem(SKIP_KEY, "1"); } catch (_) {}
    setTimeout(() => {
      setShow(false);
      onDone && onDone();
    }, 1000);
  }

  if (!show) return null;

  return (
    <div
      className={`rocket-stage${gone ? " gone" : ""}`}
      aria-hidden="true"
      data-testid="rocket-intro"
    >
      <RocketStars />
      <button
        type="button"
        className="rocket-skip"
        onClick={finish}
        data-testid="rocket-skip"
      >Skip Intro →</button>

      <div className="rocket-launchpad" />
      <div className="rocket-craft">
        <div className="body">
          <span className="fin l" />
          <span className="fin r" />
        </div>
        <div className="flame" />
      </div>
      <div className="rocket-text">
        <span>Welcome to <em>2High</em></span>
        <small>— Entering the loft —</small>
      </div>
    </div>
  );
}

/* Background twinkle stars while rocket flies — keeps the feel consistent
   with the cosmos canvas that takes over after */
function RocketStars() {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    let raf;
    let stars = [];
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    function resize() {
      cv.width = window.innerWidth * dpr;
      cv.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(450, Math.round((window.innerWidth * window.innerHeight) / 4000));
      stars = new Array(count).fill(0).map(() => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        s: Math.random() < 0.85 ? 0.5 + Math.random() * 0.6 : 1.2 + Math.random() * 0.8,
        a: 0.45 + Math.random() * 0.5,
        ph: Math.random() * Math.PI * 2,
        sp: 0.0009 + Math.random() * 0.0022,
      }));
    }
    function frame() {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      stars.forEach((s) => {
        s.ph += s.sp * 16;
        const tw = 0.7 + Math.sin(s.ph) * 0.3;
        ctx.globalAlpha = s.a * tw;
        ctx.fillStyle = "#fff";
        ctx.shadowColor = "#fff"; ctx.shadowBlur = s.s > 1.2 ? 5 : 0;
        ctx.fillRect(Math.round(s.x), Math.round(s.y), Math.max(1, Math.round(s.s)), Math.max(1, Math.round(s.s)));
      });
      ctx.shadowBlur = 0; ctx.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    }
    resize();
    window.addEventListener("resize", resize);
    raf = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} />;
}
